import Button from '@atoms/Button/Button';
import Carousel from '@atoms/Carousel/Carousel';
import Cta from '@atoms/Cta/Cta';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { ICampaignBlock } from '@models/ICampaignBlock';
import Countdown from '@molecules/Countdown/Countdown';
import { Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';

const CampaignBlockContent = ({
  meta: { contentLink },
  items,
  leftArrowLabel,
  rightArrowLabel,
}: Readonly<ICampaignBlock>) => (
  <div className="tw-component-margin tw-relative tw-max-w-[100vw]">
    <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-1/2 tw-z-[2] tw-mx-12 -tw-translate-y-1/2 max-md:tw-hidden">
      <div className=" tw-mx-auto tw-flex tw-w-full tw-max-w-screen-xxl tw-justify-between">
        <Button
          iconName="chevron-left"
          id={`prevBtn-${contentLink}`}
          modifier="outline"
          variant="white"
          aria-label={leftArrowLabel}
          iconClasses="tw-duration-0"
          buttonClasses="tw-pointer-events-auto"
        />
        <Button
          iconName="chevron-right"
          id={`nextBtn-${contentLink}`}
          modifier="outline"
          variant="white"
          aria-label={rightArrowLabel}
          iconClasses="tw-duration-0"
          buttonClasses="tw-pointer-events-auto"
        />
      </div>
    </div>
    <div>
      <Carousel
        id={contentLink}
        navId={contentLink}
        pagination={{
          el: `#campaign-block-${contentLink}`,
          bulletClass:
            'tw-h-3 tw-w-3 tw-flex-shrink-0 tw-rounded-full tw-bg-grey-20',
        }}
        className="overflow-xxl tw-w-full"
        watchSlidesProgress
        additionalModules={items.length > 1 ? [Pagination] : undefined}
        loop
      >
        {items.map((item, index) => (
          <SwiperSlide
            className="stretch-card tw-dark tw-relative tw-h-full max-xxl:tw-min-h-[400px]"
            key={`${item.title}-${index}`}
          >
            <div className="tw-mx-auto tw-max-w-screen-xxl before:tw-absolute before:tw-inset-0 before:tw-z-[1] before:tw-bg-gradient-to-t before:tw-from-black/50 before:tw-from-80%">
              <div className="tw-relative tw-z-[1] tw-mx-4 tw-my-20 tw-flex tw-flex-col tw-gap-6 tw-text-white md:tw-mx-[152px] md:tw-max-w-md lg:tw-mx-32 xl:tw-mx-40 xl:tw-max-w-[528px] xxl:tw-mx-44 xxl:tw-max-w-[608px]">
                {item.title && (
                  <Text variant="heading-28" as="h2">
                    {item.title}
                  </Text>
                )}
                {item.description && (
                  <Text variant="body-small">{item.description}</Text>
                )}
                {!!item.countdown?.expiryDate && (
                  <Countdown {...item.countdown} />
                )}
                {!!item.links?.length && (
                  <div className="tw-flex tw-flex-wrap tw-gap-6">
                    {item.links.map((link) => (
                      <Cta
                        key={link.url}
                        url={link.url}
                        target={link.target}
                        text={link.text}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Image
              image={item.image}
              classes="tw-absolute tw-inset-0 tw-z-0"
              imageClasses="tw-object-cover tw-h-full tw-w-full"
              sizes="100vw"
            />
          </SwiperSlide>
        ))}
      </Carousel>
      <div
        id={`campaign-block-${contentLink}`}
        className="tw-absolute tw-bottom-6 tw-left-0 tw-right-0 tw-z-[1] tw-flex tw-justify-center tw-gap-[12px]"
      />
    </div>
  </div>
);

const CampaignBlock = (props: Readonly<ICampaignBlock>) => (
  <>
    {/* Display on full width layout */}
    <section className="group-[.rich-text--article]/article:tw-hidden">
      <CampaignBlockContent {...props} />
    </section>
    {/* Display on article page, designed to break out of article max width */}
    <section className="columns--max-width-md--escape tw-hidden group-[.rich-text--article]/article:tw-block max-md:-tw-mx-4">
      <CampaignBlockContent
        {...props}
        meta={{ contentLink: `${props.meta.contentLink}-article` }}
      />
    </section>
  </>
);
export default CampaignBlock;
